import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Descriptions,
  Form,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography
} from 'antd';
import { ArrowLeftOutlined, EditOutlined, IdcardOutlined, WalletOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import SupplierForm from '../components/suppliers/SupplierForm';
import SupplierDefaultCostsTab from '../components/suppliers/SupplierDefaultCostsTab';
import { applySupplierSaveError } from '../components/suppliers/supplierFormErrors';
import { canManageSuppliers } from '../modules/suppliers/supplierAccessPolicy';
import { normalizeSupplierContacts } from '../utils/supplierContacts';
import type { Supplier, SupplierSubmitValues } from '../modules/suppliers/types';
import { useFeedback } from '../utils/feedback';

const { Text, Title } = Typography;

const EMPTY_VALUE = '—';

const formatDateTime = (value?: string) => (value ? new Date(value).toLocaleString('pl-PL') : EMPTY_VALUE);

const SupplierDetail: React.FC = () => {
  const { t } = useTranslation(['suppliers', 'common']);
  const feedback = useFeedback();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const currentUser = authService.getCurrentUser();
  const canEditSuppliers = canManageSuppliers(currentUser);

  const fetchSupplier = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await api.get(`/suppliers/${id}`);
      setSupplier(response.data);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        feedback.warning(t('suppliers:detail.notFound'));
        navigate('/app/suppliers', { replace: true });
        return;
      }
      feedback.error(t('suppliers:messages.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupplier();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleUpdate = async (values: SupplierSubmitValues) => {
    if (!id) return;
    setSubmitting(true);
    form.setFields([
      { name: 'name', errors: [] },
      { name: 'contactPeople', errors: [] },
      { name: 'taxId', errors: [] }
    ]);

    try {
      await api.put(`/suppliers/${id}`, values);
      feedback.success(t('suppliers:messages.updateSuccess', { name: values.name }));
      setEditing(false);
      await fetchSupplier();
    } catch (error) {
      applySupplierSaveError(error, form, t, feedback, t('suppliers:messages.saveFailed', { name: values.name }));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!supplier) {
    return null;
  }

  const renderValue = (value?: string | null) =>
    value ? value : <Text type="secondary">{EMPTY_VALUE}</Text>;

  const contacts = normalizeSupplierContacts(
    supplier.contactPeople,
    supplier.contactPerson,
    supplier.phone,
    supplier.email
  );

  const dataTab = editing ? (
    <Card>
      <SupplierForm
        form={form}
        editingSupplier={supplier}
        submitting={submitting}
        submitLabel={t('common:save')}
        onSubmit={handleUpdate}
        open={editing}
        onCancel={() => setEditing(false)}
      />
    </Card>
  ) : (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {canEditSuppliers && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
            {t('common:edit')}
          </Button>
        </div>
      )}

      <Card title={t('suppliers:detail.basicInfo')}>
        <Descriptions column={1} size="small">
          <Descriptions.Item label={t('suppliers:form.taxId')}>
            {renderValue(supplier.taxId)}
          </Descriptions.Item>
          <Descriptions.Item label={t('suppliers:form.defaultCurrency')}>
            {supplier.defaultCurrency || 'PLN'}
          </Descriptions.Item>
          <Descriptions.Item label={t('suppliers:form.address')}>
            {renderValue(supplier.address)}
          </Descriptions.Item>
          <Descriptions.Item label={t('suppliers:detail.orderCount')}>
            {supplier.orderCount ?? 0}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('suppliers:detail.contacts')}>
        <Descriptions column={1} size="small">
          <Descriptions.Item label={t('suppliers:table.contactPeople')}>
            {contacts.length > 0 ? (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {contacts.map((contact, index) => (
                  <div key={`${supplier.id}-contact-${index}`}>
                    <Text strong>{contact.name || EMPTY_VALUE}</Text>
                    {contact.phone ? ` · ${contact.phone}` : ''}
                    {contact.email ? ` · ${contact.email}` : ''}
                  </div>
                ))}
              </Space>
            ) : (
              renderValue(null)
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card>
        <Descriptions column={1} size="small">
          <Descriptions.Item label={t('suppliers:detail.createdAt')}>
            {formatDateTime(supplier.createdAt)}
          </Descriptions.Item>
          <Descriptions.Item label={t('suppliers:detail.updatedAt')}>
            {formatDateTime(supplier.updatedAt)}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </Space>
  );

  const tabItems = [
    { key: 'data', label: t('suppliers:detail.dataTab'), children: dataTab },
    // Default-cost read access on the backend is scoped to the same
    // ADMIN/OPERATIONS roles as canManageSuppliers (requireSupplierManagement
    // gates GET too, not just write) — hide the tab entirely for other roles
    // instead of rendering a tab that always errors on load.
    ...(canEditSuppliers
      ? [{
          key: 'defaultCosts',
          label: t('suppliers:detail.defaultCostsTab'),
          children: <SupplierDefaultCostsTab supplierId={supplier.id} canEdit={canEditSuppliers} />
        }]
      : [])
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/suppliers')}>
            {t('common:back')}
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            {supplier.name}
          </Title>
          <Tag icon={<IdcardOutlined />} bordered={false}>
            {supplier.taxId || t('suppliers:meta.noTaxId')}
          </Tag>
          <Tag icon={<WalletOutlined />} bordered={false}>
            {supplier.defaultCurrency || 'PLN'}
          </Tag>
        </div>

        <Tabs items={tabItems} />
      </Space>
    </div>
  );
};

export default SupplierDetail;
